import React, { useEffect, useState } from "react";
import { PaziaButton, PaziaInlineLoader } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { RETAINED_REVENUE_MODE, RetainedRevenueChoice } from "api/registrations";
import { ModalPadding } from "../Modals/ModalPadding";
import { ModalHeader } from "../Modals/ModalHeader";
import { isRetainedRevenueChoiceValid, RetainedRevenueFields } from "./RetainedRevenueFields";

interface RetainedRevenueModalProps {
  id: string;
  isOpen: boolean;
  title: string;
  description?: string;
  confirmText: string;
  /** Amounts of a single registration. Omit for a bulk removal (the server resolves amounts per student). */
  amounts?: { paidAmount: number; advanceAmount: number };
  loading?: boolean;
  initialChoice?: RetainedRevenueChoice;
  onConfirm: (choice: RetainedRevenueChoice) => Promise<void>;
  onCloseRequest: () => void;
}

export const RetainedRevenueModal = ({
  id,
  isOpen,
  title,
  description,
  confirmText,
  amounts,
  loading = false,
  initialChoice,
  onConfirm,
  onCloseRequest,
}: RetainedRevenueModalProps) => {
  const [choice, setChoice] = useState<RetainedRevenueChoice>({ mode: RETAINED_REVENUE_MODE.NONE });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) setChoice(initialChoice ?? { mode: RETAINED_REVENUE_MODE.NONE });
    // Reset only when the modal (re)opens, so a refetch of the parent doesn't wipe the admin's pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isValid = isRetainedRevenueChoiceValid(choice, amounts?.paidAmount);

  const confirm = async () => {
    if (!isValid || processing) return;
    setProcessing(true);
    try {
      await onConfirm(choice);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <PaziaModal id={id} isOpen={isOpen} onCloseRequest={onCloseRequest} size="lg">
      <ModalPadding>
        <ModalHeader>{title}</ModalHeader>
        {description && <p className="mb-4 text-sm">{description}</p>}
        {loading ? (
          <div className="flex justify-center py-6">
            <PaziaInlineLoader />
          </div>
        ) : (
          <RetainedRevenueFields
            value={choice}
            onChange={setChoice}
            paidAmount={amounts?.paidAmount}
            advanceAmount={amounts?.advanceAmount}
          />
        )}
        <div className="mt-6 flex justify-end gap-2">
          <PaziaButton title="Anuluj" type="tertiary" btnType="button" onClick={onCloseRequest} />
          <PaziaButton
            title={confirmText}
            type="primary"
            btnType="button"
            disabled={loading || !isValid}
            processing={processing}
            onClick={confirm}
          />
        </div>
      </ModalPadding>
    </PaziaModal>
  );
};
